import React from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';

function renderServerPaginationEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Pagination Settings</h4>
      <p className="af-properties-tab__help" style={{ marginBottom: '10px' }}>
        Renders real Prev / Next + "Page N" controls that re-run the form's own
        'search'-triggered apiAction with an incremented/decremented page number — for
        server-paginated results, where the backend returns one page of rows at a time.
        Not client-side paging (that's the grid control's own <code>features.pagination</code>
        / <code>config.pageSize</code>, for a full row set the grid already has in hand).
      </p>
      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Results grid control ID
          <input
            type="text"
            value={(control.config?.resultsControlID as string) || ''}
            onChange={(e) => onConfigChange('resultsControlID', e.target.value || undefined)}
            className="af-properties-tab__input"
            placeholder="e.g. results-grid"
          />
        </label>
        <p className="af-properties-tab__help">
          Optional. The ID of this form's `grid` control showing the current page's rows.
          When set, Next disables once that grid's row count comes back empty on a page
          past the first — a signal we've paged past the last page. Leave blank to always
          keep Next enabled.
        </p>
      </div>
      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Total count control ID
          <input
            type="text"
            value={(control.config?.totalControlID as string) || ''}
            onChange={(e) => onConfigChange('totalControlID', e.target.value || undefined)}
            className="af-properties-tab__input"
            placeholder="e.g. total-count"
          />
        </label>
        <p className="af-properties-tab__help">
          Optional. The ID of a (typically hidden) control holding the search response's
          total record count, populated via the same `apiActions[].payloadMapping` mechanism
          the results grid control ID above uses. When set, shows "Page X of Y" instead of
          just "Page X", and disables Next once the last page is reached.
        </p>
      </div>
      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Page size control ID
          <input
            type="text"
            value={(control.config?.pageSizeControlID as string) || ''}
            onChange={(e) => onConfigChange('pageSizeControlID', e.target.value || undefined)}
            className="af-properties-tab__input"
            placeholder="e.g. page-size"
          />
        </label>
        <p className="af-properties-tab__help">
          Optional. The ID of a `select` control (typically configured with
          `config.triggerSearchOnChange: true`) holding the current page size, used together
          with the total count above to compute total pages. Defaults to
          `config.defaultPageSize` (or 20) when not set.
        </p>
      </div>
      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Default page size
          <input
            type="number"
            min={1}
            value={(control.config?.defaultPageSize as number) ?? ''}
            onChange={(e) => onConfigChange('defaultPageSize', e.target.value ? Number(e.target.value) : undefined)}
            className="af-properties-tab__input"
            placeholder="20"
          />
        </label>
        <p className="af-properties-tab__help">
          Used to compute total pages when no page size control ID is set (or while it has
          no value yet).
        </p>
      </div>
    </div>
  );
}

export const ServerPaginationEditor: PropertyEditor = {
  type: 'server-pagination',
  label: 'Server Pagination',
  description: 'Configure the results grid this pager is bound to',
  render: renderServerPaginationEditor,
};
